export const PROFILE_PATH = '/enterprise/profile';
export const OPERATION_PATH = '/enterprise/operation';
export const PARKS_LIST_PATH = '/parks/list';
export const parkRelationPath = (parkUuid: string) => `/parks/list/${parkUuid}`;
export const parkExitProgressPath = (parkUuid: string, uuid: string) =>
  `/parks/list/${parkUuid}/exits/${uuid}`;
export const APPLICATIONS_PATH = '/parks/applications';
export const APPLICATIONS_NEW_PATH = '/parks/applications/new';
export const AUTHORIZATION_PATH = '/parks/authorization';
export const LEASES_PATH = '/parks/leases';
export const INVITATIONS_PATH = '/parks/invitations';
export const WORKBENCH_PATH = '/workbench';
export const MATERIALS_PATH = '/data/materials';
export const USAGE_PATH = '/data/usage';
export const SETTINGS_PERMISSIONS_PATH = '/settings/permissions';

export const buildApplicationLocation = (applicationUuid: string) => ({
  pathname: `${APPLICATIONS_PATH}/${encodeURIComponent(applicationUuid)}`,
  search: '',
});

export const buildAdmissionLocation = (parkUuid: string) => ({
  pathname: APPLICATIONS_NEW_PATH,
  search: `?parkUuid=${encodeURIComponent(parkUuid)}`,
});

export const buildProfileLocation = (parkUuid?: string) => ({
  pathname: PROFILE_PATH,
  search: parkUuid ? `?parkUuid=${encodeURIComponent(parkUuid)}` : '',
});

export const buildParksLocation = (parkUuid?: string) => ({
  pathname: APPLICATIONS_NEW_PATH,
  search: parkUuid ? `?parkUuid=${encodeURIComponent(parkUuid)}` : '',
});

export const isPublicPath = (pathname: string) =>
  pathname === '/callback' || pathname.startsWith('/user');

export const isDefaultEntryPath = (pathname: string) =>
  pathname === '/' || pathname === '/home';

export const isParkScopedPath = (pathname: string) =>
  pathname === '/services' ||
  pathname.startsWith('/services/') ||
  pathname === '/resources' ||
  pathname.startsWith('/resources/') ||
  pathname === '/data/submissions' ||
  pathname.startsWith('/data/submissions/') ||
  pathname === AUTHORIZATION_PATH ||
  pathname === LEASES_PATH ||
  pathname === USAGE_PATH;

export const isApplicationPath = (pathname: string) =>
  pathname === APPLICATIONS_PATH ||
  pathname === APPLICATIONS_NEW_PATH ||
  pathname.startsWith(`${APPLICATIONS_PATH}/`);

export const isInvitationPath = (pathname: string) =>
  pathname === INVITATIONS_PATH || pathname.startsWith(`${INVITATIONS_PATH}/`);

export const isPreSettlementPath = (pathname: string) =>
  pathname === PROFILE_PATH ||
  pathname === '/parks' ||
  pathname === PARKS_LIST_PATH ||
  isApplicationPath(pathname) ||
  isInvitationPath(pathname);

export const isUnsettledBootstrapState = (state?: string) =>
  state === 'PROFILE_REQUIRED' ||
  state === 'PARK_REQUIRED' ||
  state === 'ADMISSION_PENDING';

export const shouldShowEnterpriseSider = (state?: string) =>
  Boolean(state) && !isUnsettledBootstrapState(state);

export const isTenantScopedPath = (pathname: string) =>
  pathname === PROFILE_PATH ||
  pathname === OPERATION_PATH ||
  pathname === PARKS_LIST_PATH ||
  pathname === AUTHORIZATION_PATH ||
  pathname === MATERIALS_PATH ||
  pathname === USAGE_PATH ||
  pathname === SETTINGS_PERMISSIONS_PATH ||
  pathname.startsWith('/enterprise/') ||
  pathname.startsWith('/parks') ||
  isInvitationPath(pathname) ||
  pathname.startsWith('/settings/') ||
  isApplicationPath(pathname);

export const isMixedPath = (pathname: string) => pathname === WORKBENCH_PATH;

export const isActiveMembershipPark = (park: { relationStatus?: string }) =>
  park.relationStatus === 'active';

export const isMembershipPark = (park: { relationStatus?: string }) =>
  park.relationStatus === 'active' || park.relationStatus === 'suspended';

export const isReviewingAdmissionStatus = (status?: string) =>
  status === 'submitted' || status === 'reviewing';

export const getAdmissionProcessStatus = (park?: {
  admissionStatus?: string;
  latestAdmission?: { status?: string };
}) => {
  const latest = park?.latestAdmission?.status;
  if (latest) return latest;
  if (isReviewingAdmissionStatus(park?.admissionStatus)) {
    return park?.admissionStatus;
  }
  return undefined;
};

export const getParkOccupancyLabel = (park: {
  relationStatus?: string;
  admissionStatus?: string;
  latestAdmission?: { status?: string };
}) => {
  if (park.relationStatus === 'active') {
    return {
      kind: 'active' as const,
      text: '已入驻',
      color: 'success' as const,
    };
  }
  if (park.relationStatus === 'suspended') {
    return {
      kind: 'suspended' as const,
      text: '已暂停',
      color: 'default' as const,
    };
  }
  const process = getAdmissionProcessStatus(park);
  if (isReviewingAdmissionStatus(process)) {
    return {
      kind: 'reviewing' as const,
      text: '审核中',
      color: 'orange' as const,
    };
  }
  if (process === 'approved') {
    return {
      kind: 'approved' as const,
      text: '申请已通过',
      color: 'blue' as const,
    };
  }
  return {
    kind: 'available' as const,
    text: '可申请',
    color: 'success' as const,
  };
};

export const getActiveMembershipParks = <T extends { uuid: string }>(
  parks?: Array<T & { relationStatus?: string; admissionStatus?: string }>,
  _accessibleParks?: T[],
  _state?: string,
): T[] => {
  const listed = parks ?? [];
  const activeFromBootstrap = listed.filter(isActiveMembershipPark);
  return activeFromBootstrap;
};
